import { type ReactNode } from 'react';
import { type SportName, SPORT_REGISTRY, TeamIcon } from '@bravemove/design-tokens/icons';
import { Card } from '../Card/Card';
import { Avatar } from '../Avatar/Avatar';
import { SkillLevelBadge } from '../SkillLevelBadge/SkillLevelBadge';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';

export interface TeamMemberPreview {
  readonly name: string;
  readonly avatarSrc?: string;
  readonly isCaptain?: boolean;
}

export interface TeamCardProps {
  readonly name: string;
  readonly sport: SportName;
  readonly memberCount: number;
  readonly members?: readonly TeamMemberPreview[];
  readonly averageSkill?: number;
  readonly captain?: TeamMemberPreview;
  readonly homeCaza?: string;
  readonly action?: ReactNode;
  readonly onPress?: () => void;
  readonly className?: string;
}

export function TeamCard({
  name,
  sport,
  memberCount,
  members,
  averageSkill,
  captain,
  homeCaza,
  action,
  onPress,
  className,
}: TeamCardProps) {
  const { t } = useI18n();
  const SportIcon = SPORT_REGISTRY[sport];
  return (
    <Card
      interactive={!!onPress}
      {...(onPress ? { onPress } : {})}
      pressLabel={t('common:cardUi.openTeam', { name, defaultValue: `View ${name}` })}
      className={className}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 size-12 rounded-xl bg-info/15 text-info inline-flex items-center justify-center">
          <SportIcon size={28} color="currentColor" dot="currentColor" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <h3 className="text-body font-bold text-ink leading-snug break-words">{name}</h3>
            {averageSkill !== undefined ? (
              <SkillLevelBadge value={averageSkill} variant="pill" size="sm" />
            ) : null}
          </div>
          <div className="mt-1 text-small text-ink-2 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <TeamIcon size={12} color="currentColor" />
              <span className="tabular-nums">
                {t('common:cardUi.members', {
                  count: memberCount,
                  defaultValue: `${memberCount} member${memberCount === 1 ? '' : 's'}`,
                })}
              </span>
            </span>
            {homeCaza ? <span className="text-ink-3">· {homeCaza}</span> : null}
          </div>
        </div>
      </div>

      {captain || (members && members.length > 0) ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {captain ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Avatar name={captain.name} src={captain.avatarSrc} size="sm" />
              <div className="min-w-0">
                <div className="text-[11px] text-ink-3 font-mono uppercase tracking-wider">
                  {t('common:cardUi.captain', { defaultValue: 'Captain' })}
                </div>
                <div className="text-[12.5px] font-semibold text-ink truncate">{captain.name}</div>
              </div>
            </div>
          ) : (
            <span className="flex-1" />
          )}
          {members && members.length > 0 ? (
            <div className={cn('flex -space-x-2 rtl:space-x-reverse')}>
              {members.slice(0, 5).map((m, i) => (
                <span key={i} className="ring-2 ring-surface rounded-full">
                  <Avatar name={m.name} src={m.avatarSrc} size="xs" />
                </span>
              ))}
              {members.length > 5 ? (
                <span className="ring-2 ring-surface rounded-full size-6 bg-surface-2 text-ink-2 text-[10px] font-bold inline-flex items-center justify-center">
                  +{members.length - 5}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {action ? (
        <div className="mt-4 pt-4 border-t border-hairline flex flex-wrap items-center justify-end gap-3">
          {action}
        </div>
      ) : null}
    </Card>
  );
}
